import React, { useState } from "react";
import { GripVertical, ChevronRight, ChevronDown, X, Bookmark, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

export const PivotChip = ({ id, label, onRemove, testid }) => (
  <span draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", id)} data-testid={testid}
    className="inline-flex items-center gap-1 rounded-lg border border-[#E3DED4] bg-white px-2 py-1 text-xs font-semibold text-[#45423C] cursor-grab active:cursor-grabbing select-none hover:border-[#45423C]">
    <GripVertical className="h-3 w-3 text-[#B9AE9A]" />{label}
    {onRemove && <button onClick={() => onRemove(id)} className="ml-1 text-[#8A8577] hover:text-[#B4412A]" data-testid={`${testid}-remove`}><X className="h-3 w-3" /></button>}
  </span>
);

export const PivotZone = ({ title, hint, ids, labels, onDrop, onRemove, testid, max }) => {
  const [over, setOver] = useState(false);
  return (
    <div data-testid={testid} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData("text/plain")); }}
      className={`flex-1 min-w-[220px] rounded-xl border-2 border-dashed p-2 min-h-[52px] transition-colors ${over ? "border-[#C86235] bg-[#FDF6F1]" : "border-[#E3DED4] bg-[#FAF8F4]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#6F6A60] mb-1">{title}{max ? ` (έως ${max})` : ""}</div>
      <div className="flex flex-wrap gap-1.5">
        {ids.length === 0 && <span className="text-xs text-[#B9AE9A]">{hint}</span>}
        {ids.map((id) => <PivotChip key={id} id={id} label={labels[id]} onRemove={onRemove} testid={`${testid}-${id}`} />)}
      </div>
    </div>
  );
};

/* Named layouts stored in localStorage under `storeKey`; `current` is any serialisable object. */
export const SavedLayouts = ({ storeKey, current, apply, testPrefix = "layout" }) => {
  const read = () => { try { return JSON.parse(localStorage.getItem(storeKey)) || []; } catch { return []; } };
  const [list, setList] = useState(read);
  const [name, setName] = useState("");
  const persist = (l) => { setList(l); localStorage.setItem(storeKey, JSON.stringify(l)); };
  const save = () => { const n = name.trim(); if (!n) return; persist([...list.filter((x) => x.name !== n), { name: n, layout: current }]); setName(""); toast.success(`Αποθηκεύτηκε η διάταξη «${n}»`); };
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`${testPrefix}-saved`}>
      <Bookmark className="h-3.5 w-3.5 text-[#C86235]" />
      {list.map((l) => (
        <span key={l.name} className="inline-flex items-center rounded-lg border border-[#E3DED4] bg-white overflow-hidden">
          <button data-testid={`${testPrefix}-apply-${l.name}`} onClick={() => apply(l.layout)} className="px-2 py-1 font-semibold text-[#45423C] hover:bg-[#FAF8F4]">{l.name}</button>
          <button data-testid={`${testPrefix}-del-${l.name}`} onClick={() => persist(list.filter((x) => x.name !== l.name))} className="px-1.5 py-1 text-[#8A8577] hover:text-[#B4412A] border-l border-[#E3DED4]"><Trash2 className="h-3 w-3" /></button>
        </span>
      ))}
      <input data-testid={`${testPrefix}-name`} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Όνομα διάταξης…" className="rounded-lg border border-[#E3DED4] bg-white px-2 py-1 outline-none focus:border-[#45423C] w-40" />
      <button data-testid={`${testPrefix}-save`} onClick={save} disabled={!name.trim()} className="rounded-lg bg-[#45423C] text-white px-2.5 py-1 font-semibold disabled:opacity-40">Αποθήκευση</button>
    </div>
  );
};

/* Hierarchical rows with subtotals: tree = [{key, cells, total, children?}]
   renderCell(value, isGroup) -> array of <td>; onDrill(node, pathKeys, colKey|null) optional */
export const PivotTreeRows = ({ tree, colKeys, renderCell, labelOf, rowDims, open, toggle, onDrill, testPrefix = "pv" }) => {
  const cells = (n, group, keys) => (
    <>
      {colKeys.map((c) => <React.Fragment key={c}>{renderCell(n.cells[c], group, onDrill && (() => onDrill(n, keys, c)))}</React.Fragment>)}
      {renderCell(n.total, true, onDrill && (() => onDrill(n, keys, null)))}
    </>
  );
  const render = (nodes, depth, path, keys) => nodes.flatMap((n) => {
    const p = `${path}/${n.key}`; const k2 = [...keys, n.key]; const leaf = !n.children; const isOpen = open[p] !== false;
    const row = (
      <tr key={p} data-testid={`${testPrefix}-node-${depth}-${n.key}`} onClick={() => !leaf && toggle(p)}
        className={`border-t border-[#F1EDE6] ${leaf ? "hover:bg-[#FAF8F4]" : "cursor-pointer bg-[#FCFBF8] hover:bg-[#F7F5F1]"}`}>
        <td className="px-3 py-1.5" style={{ paddingLeft: 12 + depth * 18 }}>
          <span className={`inline-flex items-center gap-1.5 ${leaf ? "text-[#45423C]" : "font-semibold text-[#45423C]"}`}>
            {!leaf ? (isOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#C86235]" /> : <ChevronRight className="h-3.5 w-3.5 text-[#B9AE9A]" />) : <span className="w-3.5" />}
            <span className="max-w-[360px] truncate" title={n.key}>{labelOf(rowDims[depth], n.key)}</span>
            {!leaf && <span className="text-[10px] font-normal text-[#9A948A]">({n.children.length})</span>}
          </span>
        </td>
        {cells(n, !leaf, k2)}
      </tr>
    );
    if (leaf || !isOpen) return [row];
    const kids = render(n.children, depth + 1, p, k2);
    const subtotal = (
      <tr key={`${p}/__sub`} className="border-t border-[#E3DED4] bg-[#F3F0EA] text-[#6F6A60]" data-testid={`${testPrefix}-subtotal-${depth}-${n.key}`}>
        <td className="px-3 py-1 text-[11px] italic" style={{ paddingLeft: 12 + depth * 18 }}>Σύνολο {labelOf(rowDims[depth], n.key)}</td>
        {cells(n, true, k2)}
      </tr>
    );
    return [row, ...kids, subtotal];
  });
  return render(tree, 0, "", []);
};
